import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Route, Routes, useLocation, Link } from 'react-router-dom';

import { LogOut, User, X } from 'lucide-react';

import {
  AdvisorOnboardingHeader,
  AdvisorOnboardingFooter,
  AdvisorOnboardingBasicInfoStep,
  AdvisorOnboardingStartTrialStep,
  AdvisorOnboardingInviteClientStep,
  AdvisorOnboardingConnectEmailStep,
  useConsentStep,
  AdvisorOnboardingConsentStep,
  StepCompletedOverlay,
  useSubscriptionCheck,
  useOnboardingNavigation,
  ONBOARDING_STEP_CONFIGS,
} from '@client/features/advisor-onboarding';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@client/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@client/components/ui/dropdown-menu';
import { Button } from '@client/components/ui/button';

import { selectSession } from '@client/model/selectors';
import { useAdvisorOnboarding } from '@client/hooks/useAdvisorOnboarding';
import { useAdvisorOnboardingBasicInfoForm } from '@client/hooks/useAdvisorOnboardingBasicInfoForm';
import { useAdvisorConsents } from '@client/hooks/useAdvisorConsents';
import { useSignOut } from '@client/components/hooks/use-sign-out';
import { toast } from 'sonner';
import { SessionType } from '@shared';

export function AdvisorOnboarding({ advisorId }: AdvisorIdProps) {
  const location = useLocation();
  const handleSignOut = useSignOut();
  const session = useSelector(selectSession);
  const { advisor } = session;
  const isWizardSession = session.sessionType === SessionType.wizard;

  const advisorName = advisor
    ? `${advisor.advisorFirstName ?? ''} ${advisor.advisorLastName ?? ''}`.trim()
    : null;
  const advisorEmail = advisor?.advisorEmail ?? null;

  const {
    markStepAsCompleted,
    isStepCompleted,
    completedSteps,
    isLoading: isOnboardingLoading,
  } = useAdvisorOnboarding(advisorId);

  const basicInfoForm = useAdvisorOnboardingBasicInfoForm();
  const consentStep = useConsentStep();

  const {
    hasAgreedToTermsOfService,
    hasAgreedToSmsConsent,
    saveRequiredConsents,
    isSaving: isConsentSaving,
    isLoading: isConsentsLoading,
  } = useAdvisorConsents({ advisorId });

  const isStepperLoading = isOnboardingLoading || isConsentsLoading;

  const { hasActiveSubscription, hasPendingInvitation } = useSubscriptionCheck({ advisorId });

  const currentStep: AdvisorOnboardingUIStep = useMemo(() => {
    const path = location.pathname;
    const step = ONBOARDING_STEP_CONFIGS.find(s => path.endsWith(s.path));
    return step?.id ?? 'basic-info';
  }, [location.pathname]);

  const isEmailConnected = !!(
    advisor?.advisorOauthSettings?.google?.userEmail ||
    advisor?.advisorOauthSettings?.microsoft?.userEmail
  );

  const stepsForHeader = useMemo(() => {
    return ONBOARDING_STEP_CONFIGS.map(step => {
      let isCompleted = false;

      if (step.id === 'consent') {
        isCompleted = hasAgreedToTermsOfService && hasAgreedToSmsConsent;
      } else if (step.id === 'activate-seat') {
        isCompleted = isStepCompleted(step.id);
      } else if (step.id === 'connect-email') {
        isCompleted = isEmailConnected;
      } else {
        isCompleted = isStepCompleted(step.id);
      }

      return {
        id: step.id,
        title: step.title,
        isCompleted,
      };
    });
  }, [hasAgreedToTermsOfService, hasAgreedToSmsConsent, isStepCompleted, isEmailConnected]);

  const navigation = useOnboardingNavigation({
    advisorId,
    currentStep,
    markStepAsCompleted,
  });

  const handleStepClick = useCallback(
    (stepId: string) => {
      navigation.goToStep(stepId as AdvisorOnboardingUIStep);
    },
    [navigation],
  );
  const stepFlags = useMemo(() => {
    const isCurrentStepCompleted = isStepCompleted(currentStep);

    // Wizard users can skip activate-seat, consent, connect-email, and first-plan steps
    // connect-email step is always skippable for all users
    const isSkippableStepForWizard =
      currentStep === 'activate-seat' || currentStep === 'consent' || currentStep === 'first-plan';
    const isConnectEmailStep = currentStep === 'connect-email';
    const canSkip = (isWizardSession && isSkippableStepForWizard) || isConnectEmailStep;

    return {
      isNextDisabled:
        (currentStep === 'basic-info' && !basicInfoForm.isProfileComplete) ||
        (currentStep === 'consent' &&
          !(
            (consentStep.hasConsentedToTos || hasAgreedToTermsOfService) &&
            (consentStep.hasConsentedToSms || hasAgreedToSmsConsent)
          )) ||
        (currentStep === 'activate-seat' && !hasActiveSubscription) ||
        (currentStep === 'connect-email' && !isEmailConnected) ||
        (currentStep === 'first-plan' && !isStepCompleted('invite-client')),
      hideSkip: !canSkip,
      hideSkipToDashboard: !isWizardSession,
      isCurrentStepCompleted,
    };
  }, [
    currentStep,
    basicInfoForm.isProfileComplete,
    consentStep.hasConsentedToTos,
    consentStep.hasConsentedToSms,
    hasAgreedToTermsOfService,
    hasAgreedToSmsConsent,
    hasActiveSubscription,
    isEmailConnected,
    isStepCompleted,
    isWizardSession,
  ]);

  const handleNext = useCallback(async () => {
    if (currentStep === 'first-plan') {
      // Refresh session to update forceRedirect, then navigate once it's cleared
      navigation.refreshSessionAndGoToDashboard();
      return;
    }

    if (currentStep === 'consent') {
      // Save any missing consents (TOS or SMS)
      const needsTos = !hasAgreedToTermsOfService;
      const needsSms = !hasAgreedToSmsConsent;

      if (needsTos || needsSms) {
        try {
          await saveRequiredConsents();
        } catch (error) {
          console.error('Failed to save consents:', error);
          const errorMessage =
            error instanceof Error ? error.message : 'An unexpected error occurred';
          toast.error('Failed to save consents', {
            description: `${errorMessage}. Please try again.`,
          });
          return;
        }
      }
      navigation.goNext();
      return;
    }

    navigation.goNext();
  }, [
    currentStep,
    navigation,
    saveRequiredConsents,
    hasAgreedToTermsOfService,
    hasAgreedToSmsConsent,
  ]);

  return (
    <div className="relative flex min-h-screen flex-col bg-white">
      <div className="absolute right-4 top-4 z-30 flex items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-700"
              aria-label="User menu"
            >
              <User className="size-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-56"
          >
            {(advisorName || advisorEmail) && (
              <>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    {advisorName && (
                      <p className="text-sm font-medium leading-none">{advisorName}</p>
                    )}
                    {advisorEmail && (
                      <p className="text-muted-foreground text-xs leading-none">{advisorEmail}</p>
                    )}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem
              onClick={handleSignOut}
              className="cursor-pointer"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {!stepFlags.hideSkipToDashboard && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={`/advisors/${advisorId}/clients`}
                  className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                  aria-label="Go to dashboard"
                >
                  <X className="size-6" />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="left">Go to dashboard</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      <AdvisorOnboardingHeader
        currentStepId={currentStep}
        title={navigation.currentStepConfig?.title}
        steps={stepsForHeader}
        onStepClick={handleStepClick}
        isLoading={isStepperLoading}
      />

      <div className="m-auto size-full max-w-5xl p-6">
        <Routes>
          <Route
            path="basic-info"
            element={
              <AdvisorOnboardingBasicInfoStep
                form={basicInfoForm.form}
                onSubmit={basicInfoForm.onSubmit}
                isSubmitting={basicInfoForm.isSubmitting}
              />
            }
          />
          <Route
            path="activate-seat"
            element={<AdvisorOnboardingStartTrialStep advisorId={advisorId} />}
          />
          <Route
            path="consent"
            element={
              <div className="relative">
                <AdvisorOnboardingConsentStep
                  hasConsentedToTos={consentStep.hasConsentedToTos || hasAgreedToTermsOfService}
                  hasConsentedToSms={consentStep.hasConsentedToSms || hasAgreedToSmsConsent}
                  onTosConsentChange={consentStep.setHasConsentedToTos}
                  onSmsConsentChange={consentStep.setHasConsentedToSms}
                  isSubmitting={isConsentSaving}
                  hasScrolledToBottom={consentStep.hasScrolledToBottom}
                  onScrolledToBottom={consentStep.handleScrolledToBottom}
                />
                <StepCompletedOverlay
                  isVisible={hasAgreedToTermsOfService && hasAgreedToSmsConsent}
                  title="Consents accepted"
                  onContinue={navigation.goNext}
                />
              </div>
            }
          />
          <Route
            path="connect-email"
            element={<AdvisorOnboardingConnectEmailStep />}
          />
          <Route
            path="first-plan"
            element={<AdvisorOnboardingInviteClientStep />}
          />
        </Routes>
      </div>

      <AdvisorOnboardingFooter
        totalSteps={ONBOARDING_STEP_CONFIGS.length}
        currentStep={navigation.currentStepIndex}
        onNext={handleNext}
        onSkip={navigation.skip}
        onPrev={navigation.goPrev}
        isNextDisabled={stepFlags.isNextDisabled}
        isNextLoading={
          (currentStep === 'consent' && isConsentSaving) ||
          (currentStep === 'first-plan' && navigation.isPendingDashboardNavigation)
        }
        hideSkip={stepFlags.hideSkip}
        lastStepButtonLabel={'Go to Dashboard'}
        allowSkipOnLastStep={isWizardSession}
      />
    </div>
  );
}
